import { useEffect, useState } from 'react';
import { type SubmitHandler, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { TENANT_CONST } from '@/constant';
import { VButton, VInput, VStack } from '@/package';
import { useCognitoStore } from '@/store';
import type { OnboardingForm } from '@/type';

import PrivacyAgree from '../PrivacyAgree';

const ENTERPRISE_TIER_I18N_KEY = 'pricing.enterprise.anguillidae' as const;

export const QRSignUpForm = ({
  email,
  name,
}: {
  email: string;
  name: string;
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [signupError, setSignupError] = useState<Error>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { formState, handleSubmit, register, setValue } =
    useFormContext<OnboardingForm>();
  const { signUpForQR } = useCognitoStore();
  const { isValid } = formState;
  const onSubmit: SubmitHandler<OnboardingForm> = ({
    email,
    marketingPromotion,
    personalInfoCollection,
    personalInfoTransfer,
    region,
    tenantName,
    tier,
    userName,
    termsOfService,
    consentAITraining,
  }) => {
    setIsLoading(true);

    signUpForQR({
      tenantData: {
        email,
        tenantName,
        tier:
          tier.plan === t(ENTERPRISE_TIER_I18N_KEY)
            ? TENANT_CONST.TIER.STANDARD
            : TENANT_CONST.TIER.FREETIER,
      },
      tenantRegistrationData: {
        billingUnit: '1',
        companyName: email,
        customerAWSAccountId: '',
        inviteList: '',
        jobTitle: email,
        marketingPromotion: `${marketingPromotion ?? false}`,
        productCode: '',
        region,
        registrationStatus: 'In progress',
        personalInfoCollection: `${personalInfoCollection ?? false}`,
        personalInfoTransfer: `${personalInfoTransfer ?? false}`,
        termsOfService: `${termsOfService ?? false}`,
        consentAITraining: `${consentAITraining ?? false}`,
        userName,
      },
    })
      .then(() => {
        navigate('/auth/signup/qr/complete', { replace: true });
      })
      .catch(err => {
        if (err.cause?.data?.resultCode == 1409) {
          navigate('/auth/signup/qr/failed', { replace: true });
          return false;
        } else {
          setSignupError(err);
        }
      });
  };

  useEffect(() => {
    setValue('email', email, { shouldTouch: true });
    setValue('userName', name, { shouldTouch: true });
    setValue('tenantName', email.split('@')[0], {
      shouldTouch: true,
      shouldValidate: true,
    });
  }, [email, name]);

  useEffect(() => {
    if (!(signupError instanceof Error)) return;

    throw signupError;
  }, [signupError]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <VStack
        className="pb-4 text-center text-primary-500"
        items="center"
        justify="center"
        gap={0.625}
        dangerouslySetInnerHTML={{
          __html: t('auth.ces2026SignUpFormDescription.hematoglobulin'),
        }}
      />

      <VStack gap={0.5}>
        <VStack className="w-full" gap={0.25} items="center" justify="center">
          <div>
            <VInput
              {...register('email')}
              label={t('user.emailColumn.appraiser')}
              orientation="horizontal"
              readOnly
            />
          </div>
          <div>
            <VInput
              {...register('userName')}
              label={t('user.nameColumn.verseless')}
              orientation="horizontal"
              readOnly
            />
          </div>
          <div>
            <VInput
              {...register('tenantName', {
                required: {
                  message: t('auth.workspaceNameErrorMessage.humdrummishness'),
                  value: true,
                },
              })}
              autoFocus
              comment={formState.errors.tenantName?.message}
              label={t('auth.workspaceNameLabel.devoir')}
              orientation="horizontal"
              required
              valid={formState.errors.tenantName == null}
            />
          </div>
        </VStack>

        <PrivacyAgree
          hiddenTitle
          requiredList={[
            'marketingPromotion',
            'personalInfoCollection',
            'personalInfoTransfer',
            'termsOfService',
          ]}
          onChangeAgree={termItemList => {
            termItemList.forEach(termItem => {
              setValue(termItem.name as any, termItem.checked, {
                shouldDirty: true,
                shouldTouch: true,
                shouldValidate: true,
              });
            });
          }}
        />

        <VButton className="mt-6" disabled={!isValid || isLoading}>
          {t('auth.ces2026SignUpFormButtonText.hemiprism')}
        </VButton>
      </VStack>
    </form>
  );
};

QRSignUpForm.displayName = 'QRSignUpForm';
